import {
  MutationCache,
  QueryCache,
  QueryClient,
} from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import type { IObservability } from '../createObservability';

const MAX_QUERY_RETRIES = 2;
const QUERY_STALE_TIME_MS = 60_000;

function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    if (status !== undefined && status >= 400 && status < 500) {
      return false;
    }
  }
  return failureCount < MAX_QUERY_RETRIES;
}

function keyLabel(key: unknown): string {
  try {
    const raw = JSON.stringify(key);
    return raw.length > 120 ? `${raw.slice(0, 117)}...` : raw;
  } catch {
    return String(key);
  }
}

/** QueryClient com métricas/logs. Retry/staleTime iguais ao client anterior. */
export function createInstrumentedQueryClient(obs: IObservability): QueryClient {
  const queryStarted = new WeakMap<object, number>();
  const mutationStarted = new WeakMap<object, number>();

  const queryCache = new QueryCache({
    onSuccess: (_data, query) => {
      const started = queryStarted.get(query);
      const durationMs = started !== undefined ? performance.now() - started : undefined;
      obs.metrics.increment('query.success');
      if (durationMs !== undefined) obs.metrics.gauge('query.duration_ms', durationMs);
      if (query.state.fetchFailureCount > 0) {
        obs.metrics.increment('query.retry', query.state.fetchFailureCount);
      }
      obs.logger.debug('Query success', {
        key: keyLabel(query.queryKey),
        durationMs,
        retries: query.state.fetchFailureCount,
      });
    },
    onError: (error, query) => {
      const started = queryStarted.get(query);
      const durationMs = started !== undefined ? performance.now() - started : undefined;
      obs.metrics.increment('query.error');
      if (durationMs !== undefined) obs.metrics.gauge('query.duration_ms', durationMs);
      obs.logger.error('Query error', {
        key: keyLabel(query.queryKey),
        durationMs,
        retries: query.state.fetchFailureCount,
        error: error instanceof Error ? error.message : String(error),
      });
    },
  });

  const mutationCache = new MutationCache({
    onMutate: (_variables, mutation) => {
      mutationStarted.set(mutation, performance.now());
      obs.metrics.increment('mutation.start');
      obs.logger.debug('Mutation start', { key: keyLabel(mutation.options.mutationKey) });
    },
    onSuccess: (_data, _variables, _onMutateResult, mutation) => {
      const started = mutationStarted.get(mutation);
      const durationMs = started !== undefined ? performance.now() - started : undefined;
      obs.metrics.increment('mutation.success');
      if (durationMs !== undefined) obs.metrics.gauge('mutation.duration_ms', durationMs);
      obs.logger.debug('Mutation success', {
        key: keyLabel(mutation.options.mutationKey),
        durationMs,
      });
    },
    onError: (error, _variables, _onMutateResult, mutation) => {
      const started = mutationStarted.get(mutation);
      const durationMs = started !== undefined ? performance.now() - started : undefined;
      obs.metrics.increment('mutation.error');
      if (durationMs !== undefined) obs.metrics.gauge('mutation.duration_ms', durationMs);
      obs.logger.error('Mutation error', {
        key: keyLabel(mutation.options.mutationKey),
        durationMs,
        error: error instanceof Error ? error.message : String(error),
      });
    },
  });

  const client = new QueryClient({
    queryCache,
    mutationCache,
    defaultOptions: {
      queries: {
        staleTime: QUERY_STALE_TIME_MS,
        retry: shouldRetryQuery,
      },
      mutations: {
        retry: false,
      },
    },
  });

  // ponytail: cache hit = já tinha data ao iniciar fetch; miss = sem data
  client.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated' || event.action.type !== 'fetch') return;
    if (event.query.state.fetchStatus !== 'fetching') return;

    queryStarted.set(event.query, performance.now());
    obs.metrics.increment('query.start');

    if (event.query.state.data !== undefined) {
      obs.metrics.increment('query.cache_hit');
      obs.logger.debug('Query cache hit', { key: keyLabel(event.query.queryKey) });
    } else {
      obs.metrics.increment('query.cache_miss');
      obs.logger.debug('Query cache miss', { key: keyLabel(event.query.queryKey) });
    }
  });

  return client;
}
